import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authClient } from '../lib/authClient';
import { useBandRole } from '../context/BandContext';
import { useOfflineSession } from '../context/OfflineContext';
import { getLastSession } from '../utils/lastSession';
import { useOnSignalBack } from '../utils/connectivity';

export default function ProtectedRoute({ children }) {
  const { data: session, isPending, isRefetching, error, refetch } = authClient.useSession();
  const { setBandRole, setTier } = useBandRole();
  const { offlineSession, setOfflineSession } = useOfflineSession();
  const [lookup, setLookup] = useState('idle');  // idle | loading | none

  const signedIn = !!session?.user;
  const settled = !isPending && !isRefetching;
  // The session check failed because the server can't be reached, not because the session is
  // invalid: carry on with the last signed-in user's saved data until the server is back.
  const unreachable = settled && !signedIn && (!!error || !navigator.onLine);

  useEffect(() => {
    // Back online, or the server answered that there's no valid session: leave offline mode
    if (signedIn || (settled && !unreachable)) { setOfflineSession(null); return; }
    if (!unreachable || offlineSession) return;
    let live = true;
    setLookup('loading');
    getLastSession().then(last => {
      if (!live) return;
      if (!last) { setLookup('none'); return; }
      setOfflineSession(last);
      setBandRole(last.bandRole ?? null);
      setTier(last.subscriptionTier ?? null);
    });
    return () => { live = false; };
  }, [signedIn, settled, unreachable, offlineSession, setOfflineSession, setBandRole, setTier]);

  // Keep trying the real session while offline. Offline mode stays on through each retry (the
  // session store reports "pending" while it asks), so the page doesn't flicker.
  useEffect(() => {
    if (!offlineSession) return;
    const retry = () => refetch?.();
    window.addEventListener('online', retry);
    const timer = setInterval(retry, 30000);
    return () => { window.removeEventListener('online', retry); clearInterval(timer); };
  }, [offlineSession, refetch]);
  // A request just got through, so don't wait for the next retry
  useOnSignalBack(() => { if (offlineSession) refetch?.(); });

  if (signedIn || offlineSession) return children;
  if (!settled) return null;
  if (unreachable && lookup !== 'none') return null;
  return <Navigate to="/sign-in" replace />;
}
